import { Clock, Shield, Terminal, Lock, FileText, FolderOpen } from "lucide-react";
import { useCallback, useState } from "react";
import { SeverityBadge } from "@/components/shared/SeverityBadge";
import { StatCard } from "@/components/shared/StatCard";
import { RankedList } from "@/components/monitoring/RankedList";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useGatewayRpc } from "@/hooks/useGatewayRpc";
import { usePolling } from "@/hooks/usePolling";
import { formatDuration } from "@/lib/chart-helpers";
import { incompleteNotice, probe, probeOr, unreachableNames } from "@/lib/probe";
import type { AuthDecisionEntry, ToolTraceEntry, PermissionDecisionEntry, PromptContextEntry, CaseOperationEntry, AuditSummary } from "@/types/audit";

type Tab = "summary" | "auth" | "tools" | "permissions" | "prompts" | "cases";

const DECISION_COLORS: Record<string, string> = {
  allow: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  dev_mode: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  block_no_token: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  block_invalid: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  allowed: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  blocked: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
};

export function AuditExplorer() {
  const { rpc, state } = useGatewayRpc();
  const [tab, setTab] = useState<Tab>("summary");
  const [timeWindow, setTimeWindow] = useState(3_600_000); // 1h

  const fetcher = useCallback(async () => {
    // Settled probes (lib/probe.ts): an audit trail that silently reads
    // EMPTY because the rpc failed is indistinguishable from "nothing
    // happened" — the wrong answer on an audit surface.
    const [summary, auth, tools, permissions, prompts, cases] = await Promise.all([
      probe(rpc<AuditSummary>("audit.summary", { sinceMs: timeWindow })),
      probe(rpc<AuthDecisionEntry[]>("audit.auth", { sinceMs: timeWindow })),
      probe(rpc<ToolTraceEntry[]>("audit.tools", { sinceMs: timeWindow })),
      probe(rpc<PermissionDecisionEntry[]>("audit.permissions", { sinceMs: timeWindow })),
      probe(rpc<PromptContextEntry[]>("audit.prompts", { sinceMs: timeWindow })),
      probe(rpc<CaseOperationEntry[]>("audit.cases", { sinceMs: timeWindow })),
    ]);
    const unreachable = unreachableNames({
      summary,
      auth,
      tools,
      permissions,
      prompts,
      cases,
    });
    return {
      summary: probeOr(summary, null as AuditSummary | null),
      auth: probeOr(auth, [] as AuthDecisionEntry[]),
      tools: probeOr(tools, [] as ToolTraceEntry[]),
      permissions: probeOr(permissions, [] as PermissionDecisionEntry[]),
      prompts: probeOr(prompts, [] as PromptContextEntry[]),
      cases: probeOr(cases, [] as CaseOperationEntry[]),
      unreachable,
    };
  }, [rpc, timeWindow]);

  const { data } = usePolling(fetcher, 15_000, { enabled: state === "connected" });
  const summary = data?.summary;

  /** Did THIS tab's own probe fail? Each tab's empty state ("No auth
   *  decisions") is a claim that NOTHING HAPPENED — only sayable when we
   *  actually read that source. The page banner alone does not cover it,
   *  because the operator reads the tab, not the banner. */
  const down = (name: string) => data?.unreachable.includes(name) ?? false;
  const UNREADABLE = "Could not read this source — see the notice above.";

  if (state !== "connected" && !data) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <h1 className="text-2xl font-semibold">Audit Explorer</h1>
        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
          {state === "connecting" ? "Connecting to gateway..." : "Gateway disconnected"}
        </p>
      </div>
    );
  }

  // A tab badge reading "(0)" off a failed probe is the same lie as the empty
  // state below it — show "—" so the count is never mistaken for a reading.
  const count = (name: string, n: number | undefined) => (down(name) ? "—" : (n ?? 0));

  const tabs: { id: Tab; label: string; icon: React.ReactNode }[] = [
    { id: "summary", label: "Summary", icon: <Shield className="h-3.5 w-3.5" /> },
    { id: "auth", label: `Auth (${count("auth", data?.auth.length)})`, icon: <Lock className="h-3.5 w-3.5" /> },
    { id: "tools", label: `Tools (${count("tools", data?.tools.length)})`, icon: <Terminal className="h-3.5 w-3.5" /> },
    { id: "permissions", label: `Permissions (${count("permissions", data?.permissions.length)})`, icon: <Lock className="h-3.5 w-3.5" /> },
    { id: "prompts", label: `Prompts (${count("prompts", data?.prompts.length)})`, icon: <FileText className="h-3.5 w-3.5" /> },
    { id: "cases", label: `Cases (${count("cases", data?.cases.length)})`, icon: <FolderOpen className="h-3.5 w-3.5" /> },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Audit Explorer</h1>
        <select value={timeWindow} onChange={(e) => setTimeWindow(Number(e.target.value))}
          className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-900">
          <option value={300_000}>Last 5 min</option>
          <option value={3_600_000}>Last 1 hour</option>
          <option value={86_400_000}>Last 24 hours</option>
        </select>
      </div>

      {/* A failed audit rpc otherwise renders as "No auth decisions" — i.e.
          "nothing happened", which on an audit surface is the wrong answer.
          Name what we could not read before any empty state is believed. */}
      {incompleteNotice(data?.unreachable ?? [], 6) && (
        <div
          className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200"
          data-testid="audit-explorer-incomplete"
          role="status"
        >
          <span>{incompleteNotice(data?.unreachable ?? [], 6)}</span>
        </div>
      )}

      {/* Tab bar */}
      <div className="flex gap-1 border-b border-slate-200 dark:border-slate-800">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)}
            className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium transition-colors ${
              tab === t.id ? "border-b-2 border-indigo-500 text-indigo-600 dark:text-indigo-400" : "text-slate-500 hover:text-slate-700 dark:text-slate-400"
            }`}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {/* `summary` is null ONLY when its probe failed (the rpc always returns
          an object), so a bare `&& summary` rendered a silently blank tab. */}
      {tab === "summary" && !summary && down("summary") && (
        <Card><CardContent className="pt-4">
          <p className="py-6 text-center text-sm text-slate-400">{UNREADABLE}</p>
        </CardContent></Card>
      )}

      {tab === "summary" && summary && (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
          <StatCard title="Auth Decisions" value={String(summary.auth.total)} />
          <StatCard title="Auth Blocked" value={String(summary.auth.blocked)} sparkColor={summary.auth.blocked > 0 ? "var(--color-critical)" : "var(--color-healthy)"} />
          <StatCard title="Tool Calls" value={String(summary.tools.total)} />
          <StatCard title="Tools Blocked" value={String(summary.tools.blocked)} sparkColor={summary.tools.blocked > 0 ? "var(--color-critical)" : "var(--color-healthy)"} />
          <StatCard title="Avg Tool Duration" value={`${summary.tools.avgDurationMs.toFixed(0)}ms`} />
          <StatCard title="Case Operations" value={String(summary.cases.total)} />
        </div>
      )}

      {tab === "auth" && (
        <Card><CardContent className="pt-4">
          {(data?.auth.length ?? 0) === 0 ? <p className="py-6 text-center text-sm text-slate-400">{down("auth") ? UNREADABLE : "No auth decisions"}</p> : (
            <div className="max-h-[400px] space-y-1.5 overflow-y-auto">
              {data!.auth.sort((a, b) => b.ts - a.ts).slice(0, 50).map((e, i) => (
                <div key={i} className="flex items-center gap-3 rounded border p-2 text-sm dark:border-slate-800">
                  <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium ${DECISION_COLORS[e.decision] ?? ""}`}>{e.decision}</span>
                  <span className="text-xs text-muted-foreground">{e.userId ?? "—"}</span>
                  <span className="text-xs text-muted-foreground">{e.role ?? "—"}</span>
                  <span className="ml-auto text-[10px] text-muted-foreground">{new Date(e.ts).toLocaleTimeString()}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent></Card>
      )}

      {tab === "tools" && (
        <Card><CardContent className="pt-4">
          {(data?.tools.length ?? 0) === 0 ? <p className="py-6 text-center text-sm text-slate-400">{down("tools") ? UNREADABLE : "No tool traces"}</p> : (
            <div className="max-h-[400px] space-y-1.5 overflow-y-auto">
              {data!.tools.sort((a, b) => b.ts - a.ts).slice(0, 50).map((e, i) => (
                <div key={i} className="rounded border p-2 text-sm dark:border-slate-800">
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium ${DECISION_COLORS[e.decision] ?? ""}`}>{e.decision}</span>
                    <span className="font-medium">{e.toolName}</span>
                    {e.durationMs != null && <span className="flex items-center gap-1 text-xs text-muted-foreground"><Clock className="h-3 w-3" />{e.durationMs}ms</span>}
                    <span className="ml-auto text-[10px] text-muted-foreground">{new Date(e.ts).toLocaleTimeString()}</span>
                  </div>
                  {e.error && <p className="mt-1 text-xs text-red-400">{e.error}</p>}
                  {e.resultSummary && <p className="mt-1 text-xs text-muted-foreground">{e.resultSummary.slice(0, 120)}</p>}
                </div>
              ))}
            </div>
          )}
        </CardContent></Card>
      )}

      {tab === "permissions" && (
        <Card><CardContent className="pt-4">
          {(data?.permissions.length ?? 0) === 0 ? <p className="py-6 text-center text-sm text-slate-400">{down("permissions") ? UNREADABLE : "No permission decisions"}</p> : (
            <div className="max-h-[400px] space-y-1.5 overflow-y-auto">
              {data!.permissions.sort((a, b) => b.ts - a.ts).slice(0, 50).map((e, i) => (
                <div key={i} className="flex items-center gap-3 rounded border p-2 text-sm dark:border-slate-800">
                  <Badge variant={e.allowed ? "default" : "destructive"} className="text-[10px]">{e.allowed ? "ALLOW" : "BLOCK"}</Badge>
                  <span className="font-medium">{e.toolName}</span>
                  <Badge variant="secondary" className="text-[10px]">{e.role}</Badge>
                  <span className="text-xs text-muted-foreground">{e.toolCategory}</span>
                  <span className="ml-auto text-xs text-muted-foreground">{e.reason}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent></Card>
      )}

      {tab === "prompts" && (
        <Card><CardContent className="pt-4">
          {(data?.prompts.length ?? 0) === 0 ? <p className="py-6 text-center text-sm text-slate-400">{down("prompts") ? UNREADABLE : "No prompt contexts"}</p> : (
            <div className="max-h-[400px] space-y-2 overflow-y-auto">
              {data!.prompts.sort((a, b) => b.ts - a.ts).slice(0, 20).map((e, i) => (
                <div key={i} className="rounded border p-3 text-sm dark:border-slate-800">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-[10px]">{e.contextType}</Badge>
                    <span className="text-xs text-muted-foreground">role: {e.role}</span>
                    {e.caseId && <span className="text-xs text-muted-foreground">case: {e.caseId.slice(0, 20)}</span>}
                    <span className="ml-auto text-[10px] text-muted-foreground">{new Date(e.ts).toLocaleTimeString()}</span>
                  </div>
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {e.injectedBlocks.map((b) => <Badge key={b} variant="secondary" className="text-[10px]">{b}</Badge>)}
                  </div>
                  <p className="mt-1 text-[10px] text-muted-foreground">{e.prependContextLength} chars injected</p>
                </div>
              ))}
            </div>
          )}
        </CardContent></Card>
      )}

      {tab === "cases" && (
        <Card><CardContent className="pt-4">
          {(data?.cases.length ?? 0) === 0 ? <p className="py-6 text-center text-sm text-slate-400">{down("cases") ? UNREADABLE : "No case operations"}</p> : (
            <div className="max-h-[400px] space-y-1.5 overflow-y-auto">
              {data!.cases.sort((a, b) => b.ts - a.ts).slice(0, 50).map((e, i) => (
                <div key={i} className="flex items-center gap-3 rounded border p-2 text-sm dark:border-slate-800">
                  {e.success ? <Badge variant="default" className="text-[10px]">OK</Badge> : <Badge variant="destructive" className="text-[10px]">ERR</Badge>}
                  <span className="font-mono text-xs">{e.method}</span>
                  <span className="text-xs text-muted-foreground">{e.durationMs}ms</span>
                  {e.error && <span className="text-xs text-red-400">{e.error}</span>}
                  <span className="ml-auto text-[10px] text-muted-foreground">{new Date(e.ts).toLocaleTimeString()}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent></Card>
      )}
    </div>
  );
}
